import axios from "axios";

// Create an Axios instance
const api = axios.create({
    baseURL: "/", // Set if you want a base URL!
    withCredentials: true, // Always send cookies
    validateStatus: (status) => {
        // Success
        if (status >= 200 && status < 300) return true;
        // Business errors we want to handle in try blocks
        if ([400, 403, 404, 409, 422, 429].includes(status)) return true;
        // Everything else (including 401/500) -> reject to catch
        return false;
    },
});

const searchParams = new URLSearchParams(window.location.search);
const portalId = searchParams.get("portalId");
const userId = searchParams.get("userId");

let csrfToken = null;
export const setCsrfToken = (token) => {
  csrfToken = token;
  api.defaults.headers.common['x-xsrf-token'] = token;
};

export const getCsrfToken = () => csrfToken;

// Always attach the latest CSRF token on every request (handles retries as well)
api.interceptors.request.use((config) => {
  if (csrfToken) {
    config.headers = config.headers || {};
    // Ensure we don't keep an old per-request header
    delete config.headers['x-xsrf-token'];
    config.headers['x-xsrf-token'] = csrfToken;
  }
  return config;
});

// Utility to avoid infinite loop
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

// Interceptor to handle 401 errors globally
api.interceptors.response.use(
    response => response,
    async (error) => {
        const originalRequest = error.config;
        if (
            error.response &&
            error.response.status === 401 &&
            !originalRequest._retry &&
            !originalRequest.url.endsWith('/auth/refresh-token')
        ) {
            // Mark request as retried to avoid infinite loop
            originalRequest._retry = true;

            if (isRefreshing) {
                // Wait if another refresh is in progress
                return new Promise(function (resolve, reject) {
                    failedQueue.push({ resolve, reject });
                })
                    .then(() => api(originalRequest))
                    .catch((err) => Promise.reject(err));
            }

            isRefreshing = true;

            try {
                // Attempt refresh
                await api.post('/auth/refresh-token',{
                    portalId, userId
                });

                //if server rotated CSRF during refresh, update it now
                const data = await fetch("/auth/get-csrf-token", { credentials: "include" })
                  .then((r) => r.json())
                  .catch(() => ({}));
                if (data?.csrfToken) setCsrfToken(data.csrfToken);

                processQueue(null);
                // Retry original request
                return api(originalRequest);
            } catch (refreshError) {
                processQueue(refreshError, null);
                // Optional: redirect to login
                window.location = "/connect";
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        // Not a 401 or already retried/refresh endpoint
        return Promise.reject(error);
    }
);

export default api;